import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom-v5-compat';
import { ClickAwayListener, Typography } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { LanternIcon } from 'src/assets/icons/Lantern';
import { ArrowIcon } from 'src/components/ArrowIcon';
import { BusinessBookDemoButton } from 'src/components/BusinessBookDemoButton';
import { CUSTOM_BUTTON_COLOR, CustomButton } from 'src/components/CustomButton';
import { BOOK_DEMO_ORIGIN } from 'src/data/common';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import {
  getCustomerPlan,
  getCustomerTier,
  getSubscriptionPlanLabel,
  getTierProperty,
  isPlanFromBasicCategory,
  isPlanFromBusinessCategory,
  isPlanFromDataDrivenCategory,
  MAX_SEAT_TIER,
} from 'src/data/subscription';
import { FLOATING_PLACEMENT } from 'src/data/ui';
import * as events from 'src/lib/events';
import { formatRoute } from 'src/lib/routing';
import { ROUTES } from 'src/routes';
import {
  useBusinessBookDemo,
  useShouldCustomerBookDemoForFeature,
  useUpgradeSubscriptionInfo,
} from 'src/services';
import { useCurrentCustomer, useCustomFeatureLimitsData } from 'src/store';

import { useSetupFloating } from './useSetupFloating';
import { useStyles } from './useStyles';

const getFeatureFromOrigin = (origin) => {
  switch (origin) {
    case BOOK_DEMO_ORIGIN.brandBriefsPopup:
    case BOOK_DEMO_ORIGIN.brandBriefsPage:
      return FEATURE_LIMITS.brandBriefPerCustomer;

    case BOOK_DEMO_ORIGIN.customFormulaPopup:
    case BOOK_DEMO_ORIGIN.customFormulaPage:
      return FEATURE_LIMITS.customFormulasPerCustomer;

    case BOOK_DEMO_ORIGIN.brandTermsPopup:
    case BOOK_DEMO_ORIGIN.brandTermsPage:
      return FEATURE_LIMITS.brandRulesPerCustomer;

    case BOOK_DEMO_ORIGIN.targetAudiencePopup:
    case BOOK_DEMO_ORIGIN.targetAudiencePage:
      return FEATURE_LIMITS.audiencePersonasPerCustomer;

    case BOOK_DEMO_ORIGIN.addWorkspace:
      return FEATURE_LIMITS.workspacesPerCustomer;

    case BOOK_DEMO_ORIGIN.addUser:
      return FEATURE_LIMITS.membersPerCustomer;

    case BOOK_DEMO_ORIGIN.toneOfVoicePopup:
    case BOOK_DEMO_ORIGIN.toneOfVoicePage:
      return FEATURE_LIMITS.tonesOfVoicePerCustomer;
    case BOOK_DEMO_ORIGIN.checkPlagiarism:
      return FEATURE_LIMITS.plagiarismChecksPerCustomer;

    default:
      return null;
  }
};

export const UpgradePopover = ({
  className,
  open,
  referenceElement,
  onClose,
  onClick,
  placement,
  origin,
}) => {
  const styles = useStyles();

  const currentCustomer = useCurrentCustomer();
  const currentPlan = getCustomerPlan(currentCustomer);
  const currentTier = getCustomerTier(currentCustomer);
  const isDataDriven = isPlanFromDataDrivenCategory(currentPlan);
  const isBusiness = isPlanFromBusinessCategory(currentPlan);
  const isBasic = isPlanFromBasicCategory(currentPlan);
  const isTopSeatTier = currentTier === MAX_SEAT_TIER?.[currentPlan];
  const isMemberLimit = origin === BOOK_DEMO_ORIGIN.addUser;
  const isPlagiarismLimit = origin === BOOK_DEMO_ORIGIN.checkPlagiarism;
  const [floatingElement, setFloatingElement] = useState(null);

  const feature = getFeatureFromOrigin(origin);
  const { currentCount, limit, label } = useCustomFeatureLimitsData(feature);

  const { nextPlan, checkoutUrl } = useUpgradeSubscriptionInfo({ feature });
  const shouldBookDemo = useShouldCustomerBookDemoForFeature({ feature });

  const getPlanAndTierLabel = () => {
    const seatsCount = getTierProperty(currentTier, 'seats', 0, currentPlan);
    return (
      getSubscriptionPlanLabel(currentPlan) + (seatsCount ? ' (' + seatsCount + ' seats)' : '')
    );
  };

  const getMemberLimitSubtitle = () => {
    if (isBusiness) {
      if (isTopSeatTier) {
        return 'Your plan does not support any more seats. Please contact Sales to upgrade to an Enterprise plan.';
      }
      return "You've reached the seat limit for your plan: " + getPlanAndTierLabel() + '.';
    }
    if (isDataDriven) {
      if (isTopSeatTier) {
        return 'Your plan does not support any more seats. Please upgrade your plan to add more seats.';
      }
      return "You've reached the seat limit for your plan: " + getPlanAndTierLabel() + '.';
    }
    return 'Upgrade now to get 3+ seats, unlimited performance improvements, more brand features, 5+ workspaces and more!';
  };

  const getPlagiarismLimitSubtitle = () => {
    if (isBasic) {
      return 'Upgrade now to get unlimited words and unlock our plagiarism checker tool.';
    }
    return 'Upgrade to get more plagiarism checks, and take your copywriting to the next level.';
  };

  const getButtonLabel = () => {
    if (nextPlan === currentPlan) {
      return 'Add more seats';
    }

    return isPlanFromBusinessCategory(nextPlan) ? 'Upgrade' : 'Upgrade to Data-Driven';
  };

  const { floatingStyles } = useSetupFloating({
    referenceElement,
    floatingElement,
    placement,
  });

  const { executeBookDemoFlow } = useBusinessBookDemo(origin);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();

    onClose();
  };

  const handleUpgradeClicked = () => {
    const cta = shouldBookDemo
      ? events.HUBSPOT_SUBSCRIPTION_ACTION.bookDemo
      : events.HUBSPOT_SUBSCRIPTION_ACTION.buyNow;
    const hubspotPlan = events.convertSubscriptionPlanForHubspot(currentPlan);

    events.trackHubspotEvent(events.HUBSPOT_EVENT.clickedUpgradeButton, {
      cta,
      origin,
      plan: hubspotPlan,
    });
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    handleUpgradeClicked();
    onClose();
  };

  if (!open || !referenceElement) {
    return null;
  }

  return createPortal(
    <ClickAwayListener onClickAway={handleClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={cx(
          styles.root,
          { [styles.wide]: isMemberLimit || isPlagiarismLimit },
          className
        )}
        ref={setFloatingElement}
        style={floatingStyles}
      >
        {isMemberLimit ? (
          <div className={styles.titlesContainer}>
            <Typography className={styles.title}>
              <LanternIcon className={styles.icon} />
              Add additional users
            </Typography>
            <Typography className={styles.subtitle}>{getMemberLimitSubtitle()}</Typography>
          </div>
        ) : isPlagiarismLimit ? (
          <div className={styles.titlesContainer}>
            <Typography className={styles.title}>
              <LanternIcon className={styles.icon} />
              <span>
                {isBasic ? 'Make sure your copy is unique!' : 'You’ve reached your monthly limit'}
              </span>
            </Typography>
            <Typography className={styles.subtitle}>{getPlagiarismLimitSubtitle()}</Typography>
          </div>
        ) : (
          <div className={styles.titlesContainer}>
            <div className={styles.titleWrapper}>
              <LanternIcon className={styles.icon} />
              <Typography className={styles.title}>
                You&apos;re using {currentCount}/{limit} {label}
              </Typography>
            </div>

            <Typography className={styles.subtitle}>
              To add additional {label},{' '}
              <span>
                {shouldBookDemo
                  ? 'and unlock premium Enterprise features book a demo today.'
                  : 'upgrade your plan and take your copywriting to the next level.'}
              </span>
            </Typography>
          </div>
        )}

        {shouldBookDemo ? (
          <BusinessBookDemoButton
            className={styles.button}
            onClick={executeBookDemoFlow}
            origin={origin}
            hideLabel
            fullWidth
          />
        ) : isPlagiarismLimit && isBasic ? (
          <CustomButton
            className={styles.button}
            component={Link}
            to={formatRoute(ROUTES.UPGRADE)}
            onClick={handleClick}
            color={CUSTOM_BUTTON_COLOR.upsell}
            fullWidth
          >
            Find the Right Plan for You <ArrowIcon className={styles.arrowIcon} />
          </CustomButton>
        ) : (
          <CustomButton
            className={styles.button}
            component={Link}
            to={checkoutUrl}
            onClick={handleClick}
            color={CUSTOM_BUTTON_COLOR.upsell}
            fullWidth
          >
            {getButtonLabel()} <ArrowIcon className={styles.arrowIcon} />
          </CustomButton>
        )}
      </div>
    </ClickAwayListener>,
    document.body
  );
};

UpgradePopover.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool,
  referenceElement: PropTypes.instanceOf(Element),
  onClose: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  placement: PropTypes.oneOf(Object.values(FLOATING_PLACEMENT)),
  origin: PropTypes.string,
};

UpgradePopover.defaultProps = {
  className: null,
  open: false,
  referenceElement: null,
  onClick: null,
  placement: FLOATING_PLACEMENT.bottomEnd,
  origin: null,
};
